<script setup>
import { ref, watch, onBeforeUnmount } from 'vue';
import { useFileActions } from '../../composables/useFileActions.js';

/* Vorschau der ersten Seite eines PDFs.
 *
 * pdf.js wird erst geladen, wenn tatsächlich eine Vorschau gebraucht wird —
 * die Bibliothek samt Arbeitsprozess wiegt rund ein Megabyte. Wer nur die
 * Kaderliste ansieht, lädt sie nie.
 *
 * Schlägt das Zeichnen fehl, greift die eingebaute PDF-Anzeige des Browsers.
 * Das ist kein Notnagel für Fehler, sondern für alte Geräte, auf denen die
 * Bibliothek nicht läuft.
 */

const props = defineProps({
  file: { type: Object, required: true },
});

const canvas = ref(null);
const fallbackHref = ref(null);
const loading = ref(true);
/* Natürliche Breite der Seite in CSS-Pixeln (PDF-Punkte x 96/72). Ab 1024 px begrenzt main.css
   die Vorschau darauf, damit die Spielerkarte in der breiten Spielerakte nicht hochgezogen und
   unscharf wird; auf dem Handy bleibt es bei 100 % der Breite. */
const naturalWidth = ref(null);
const failed = ref(false);

const { hrefFor } = useFileActions();
let renderTask = null;

async function render() {
  loading.value = true;
  failed.value = false;
  fallbackHref.value = null;

  const href = await hrefFor(props.file);
  if (!href) {
    loading.value = false;
    failed.value = true;
    return;
  }

  try {
    const pdfjs = await import('pdfjs-dist');
    const workerUrl = (await import('pdfjs-dist/build/pdf.worker.min.js?url')).default;
    pdfjs.GlobalWorkerOptions.workerSrc = workerUrl;

    const buffer = await (await fetch(href)).arrayBuffer();
    const pdf = await pdfjs.getDocument({ data: buffer }).promise;
    const page = await pdf.getPage(1);
    const viewport = page.getViewport({ scale: 2 });
    naturalWidth.value = Math.round((viewport.width / 2) * (96 / 72));

    const el = canvas.value;
    if (!el) return;
    el.width = viewport.width;
    el.height = viewport.height;

    renderTask = page.render({ canvasContext: el.getContext('2d'), viewport });
    await renderTask.promise;
    renderTask = null;
  } catch (err) {
    console.error('[pdf] Vorschau fehlgeschlagen:', err);
    fallbackHref.value = href;
    failed.value = true;
  } finally {
    loading.value = false;
  }
}

watch(() => props.file, render, { immediate: true, deep: true });

onBeforeUnmount(() => {
  try {
    renderTask?.cancel();
  } catch {
    /* bereits beendet */
  }
});
</script>

<template>
  <div class="card-preview" :style="naturalWidth ? { '--pdf-max': naturalWidth + 'px' } : undefined">
    <div v-if="loading" class="card-preview-loading">{{ $t('common.loading') }}</div>
    <embed
      v-else-if="failed && fallbackHref"
      :src="`${fallbackHref}#toolbar=0`"
      type="application/pdf"
    />
    <div v-else-if="failed" class="card-preview-loading">{{ $t('files.previewFailed') }}</div>
    <canvas v-show="!loading && !failed" ref="canvas"></canvas>
  </div>
</template>

<style scoped>
canvas {
  width: 100%;
  height: auto;
  display: block;
}
embed {
  width: 100%;
  min-height: 320px;
  display: block;
}
</style>
